import React, { useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";

import "./GlobalSearch.css";

interface SearchResult {
  id: string;
  type: "contract" | "application" | "affiliate" | "delinquent" | "equipment-warranty";
  title: string;
  subtitle: string;
  description: string;
  path: string;
  phoneNumber?: string;
  address?: string;
  contractNumber?: string;
  searchScore?: number;
  matchedFields?: string[];
}

// Enhanced mock search data with phone numbers and addresses
const mockSearchData: SearchResult[] = [
  // Contracts
  {
    id: "CT001",
    type: "contract",
    title: "CT001 - 山田太郎",
    subtitle: "グランドメゾン新宿 101号室",
    description: "賃貸契約 | 月額: ¥120,000 | 有効",
    path: "/contracts/CT001",
    phoneNumber: "090-1234-5678",
    address: "東京都新宿区西新宿1-2-3",
    contractNumber: "CT001"
  },
  {
    id: "CT002",
    type: "contract",
    title: "CT002 - 佐藤花子",
    subtitle: "サンシャイン渋谷 205号室",
    description: "賃貸契約 | 月額: ¥95,000 | 有効",
    path: "/contracts/CT002",
    phoneNumber: "080-9876-5432",
    address: "東京都渋谷区渋谷2-4-5",
    contractNumber: "CT002"
  },

  // Applications
  {
    id: "AP001",
    type: "application",
    title: "AP001 - 田中一郎",
    subtitle: "ロイヤルハイツ青山 302号室",
    description: "入居申請 | ステータス: 審査中",
    path: "/applications/AP001",
    phoneNumber: "070-5555-1234",
    address: "東京都港区青山3-5-7",
    contractNumber: "AP001"
  },

  // Affiliates
  {
    id: "AF001",
    type: "affiliate",
    title: "東京不動産",
    subtitle: "代表: 鈴木次郎",
    description: "加盟店 | 契約数: 45件 | 有効",
    path: "/affiliates/AF001",
    phoneNumber: "03-1234-5678",
    address: "東京都千代田区丸の内1-1-1",
    contractNumber: "AF001"
  },
  {
    id: "AF002",
    type: "affiliate",
    title: "渋谷住宅",
    subtitle: "代表: 高橋三郎",
    description: "加盟店 | 契約数: 32件 | 有効",
    path: "/affiliates/AF002",
    phoneNumber: "03-9876-5432",
    address: "東京都渋谷区道玄坂2-3-4",
    contractNumber: "AF002"
  },

  // Delinquents
  {
    id: "DL001",
    type: "delinquent",
    title: "DL001 - 中村雅子",
    subtitle: "パークタワー品川 1503号室",
    description: "滞納 | 滞納額: ¥240,000 | 3ヶ月",
    path: "/delinquents",
    phoneNumber: "080-1111-2222",
    address: "東京都港区港南2-15-1",
    contractNumber: "DL001"
  },

  // Equipment Warranties
  {
    id: "EW001",
    type: "equipment-warranty",
    title: "EW001 - エアコン保証",
    subtitle: "グランドメゾン新宿 101号室",
    description: "設備保証 | 東芝ライフスタイル | 月額: ¥850",
    path: "/equipment-warranty",
    phoneNumber: "090-1234-5678", // Same as property contact
    address: "東京都新宿区西新宿1-2-3", // Same as property
    contractNumber: "EW001"
  }
];

interface GlobalSearchProps {
  className?: string;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ className }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Enhanced search with phone number prioritization and cross-field search
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setSearchResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    const timeoutId = setTimeout(() => {
      const results = performEnhancedSearch(searchTerm);
      setSearchResults(results.slice(0, 8)); // Limit to 8 results
      setIsOpen(results.length > 0);
      setSelectedIndex(-1);
      setIsLoading(false);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Enhanced search algorithm with phone number prioritization
  const performEnhancedSearch = (query: string): SearchResult[] => {
    const searchTerm = query.toLowerCase().trim();

    // Phone number detection patterns
    const phonePatterns = [
      /^\d{3}-\d{4}-\d{4}$/, // 090-1234-5678
      /^\d{11}$/, // 09012345678
      /^\d{2}-\d{4}-\d{4}$/ // 03-1234-5678
    ];

    const isPhoneQuery = phonePatterns.some((pattern) => pattern.test(searchTerm.replace(/[-\s]/g, "")));

    const resultsWithScores = mockSearchData
      .map((item) => {
        let score = 0;
        let matchedFields: string[] = [];

        // Phone number matching (highest priority)
        if (item.phoneNumber && item.phoneNumber.replace(/[-\s]/g, "").includes(searchTerm.replace(/[-\s]/g, ""))) {
          score += isPhoneQuery ? 100 : 80;
          matchedFields.push("電話番号");
        }

        // Contract number exact match
        if (item.contractNumber && item.contractNumber.toLowerCase() === searchTerm) {
          score += 90;
          matchedFields.push("契約番号");
        } else if (item.contractNumber && item.contractNumber.toLowerCase().includes(searchTerm)) {
          score += 70;
          matchedFields.push("契約番号");
        }

        // Name matching (from title)
        if (item.title.toLowerCase().includes(searchTerm)) {
          score += 60;
          matchedFields.push("氏名・会社名");
        }

        // Address matching
        if (item.address && item.address.toLowerCase().includes(searchTerm)) {
          score += 50;
          matchedFields.push("住所");
        }

        // Property name matching (from subtitle)
        if (item.subtitle.toLowerCase().includes(searchTerm)) {
          score += 40;
          matchedFields.push("物件名");
        }

        // General description matching
        if (item.description.toLowerCase().includes(searchTerm)) {
          score += 30;
          matchedFields.push("詳細情報");
        }

        return {
          ...item,
          searchScore: score,
          matchedFields
        };
      })
      .filter((item) => item.searchScore! > 0)
      .sort((a, b) => b.searchScore! - a.searchScore!);

    return resultsWithScores;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setSelectedIndex((prev) => (prev < searchResults.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        event.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handleResultClick(searchResults[selectedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.path);
    setIsOpen(false);
    setSelectedIndex(-1);
    setSearchTerm("");
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleInputFocus = () => {
    if (searchResults.length > 0) {
      setIsOpen(true);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "contract":
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
          </svg>
        );
      case "application":
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
          </svg>
        );
      case "affiliate":
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17 7h-3V6a3 3 0 0 0-3-3H8a3 3 0 0 0-3 3v1H2v14h20V7h-5zM7 6a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1H7V6zm13 13H4V9h16v10z" />
          </svg>
        );
      case "delinquent":
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11 15h2v2h-2v-2zm0-8h2v6h-2V7zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
          </svg>
        );
      case "equipment-warranty":
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z" />
          </svg>
        );
      default:
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
        );
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "contract":
        return "契約";
      case "application":
        return "申請";
      case "affiliate":
        return "加盟店";
      case "delinquent":
        return "滞納者";
      case "equipment-warranty":
        return "設備保証";
      default:
        return "";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "contract":
        return "#1976d2";
      case "application":
        return "#388e3c";
      case "affiliate":
        return "#f57c00";
      case "delinquent":
        return "#d32f2f";
      case "equipment-warranty":
        return "#7b1fa2";
      default:
        return "#666";
    }
  };

  return (
    <div ref={searchRef} className={`global-search ${className || ""}`}>
      <div className="search-input-container">
        <div className="search-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
        </div>
        <input
          ref={inputRef}
          type="text"
          placeholder="契約番号・氏名・電話番号で検索"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          className="search-input pl-[30px]"
        />
        {isLoading && (
          <div className="search-loading">
            <div className="loading-spinner"></div>
          </div>
        )}
      </div>

      {isOpen && (
        <div className="search-dropdown">
          <div className="search-results">
            {searchResults.map((result, index) => (
              <div
                key={result.id}
                className={`search-result-item ${index === selectedIndex ? "selected" : ""}`}
                onClick={() => handleResultClick(result)}
              >
                <div className="result-icon" style={{ color: getTypeColor(result.type) }}>
                  {getTypeIcon(result.type)}
                </div>
                <div className="result-content">
                  <div className="result-title">{result.title}</div>
                  <div className="result-subtitle">{result.subtitle}</div>
                  <div className="result-description">{result.description}</div>

                  {/* Enhanced result information */}
                  <div className="result-details">
                    {result.phoneNumber && <div className="result-phone">📞 {result.phoneNumber}</div>}
                    {result.address && <div className="result-address">📍 {result.address}</div>}
                    {result.matchedFields && result.matchedFields.length > 0 && (
                      <div className="matched-fields">
                        <span className="match-label">一致: </span>
                        {result.matchedFields.map((field, idx) => (
                          <span key={idx} className="matched-field">
                            {field}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="result-meta">
                  <div className="result-type" style={{ color: getTypeColor(result.type) }}>
                    {getTypeLabel(result.type)}
                  </div>
                  {result.searchScore && result.searchScore > 80 && <div className="high-score-indicator">⭐</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
